import React, { useState, useEffect } from "react";
import {
  Box,
  styled,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Menu,
  Pagination,
} from "@mui/material";
import { toast } from "react-toastify";
import { getApiOrigin } from "@utils/apiOrigin";
import { getFacilityUsers } from "@services/UserManagement";
import {
  fetchUserPermissions,
  UserPermission,
  fetchFeatures,
  saveUserPermissions,
} from "@services/UserPermission";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table as MUITable,
  TableBody as MUITableBody,
  TableCell as MUITableCell,
  TableHead as MUITableHead,
  TableRow as MUITableRow,
  Checkbox,
  CircularProgress,
  Alert,
} from "@mui/material";
import facilityManagementService, {
  UserFacility,
} from "@services/facilityManagement";
import AddUserModal from "@components/AddUserModal/Index";
import EditUserModal from "@components/EditUserModal/Index";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { auditLogService } from "@services/auditLog";
import AffableConnectedIcon from "@components/Spaces/AffableConnectedIcon";
import { getCurrentUserId, formatUserTypeDisplayName } from "@utils/userUtils";
import {
  formatFacilityDropdownLabel,
  getSelectedFacilityFromSession,
} from "@utils/facilityUtils";
import {
  canAddByName,
  canUpdateByName,
  canDeleteByName,
  FEATURE_NAMES,
  getFeatureDisplayName,
  filterFeaturesForPermissionEditor,
} from "@utils/permissions";
type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  status?: boolean; // association_is_active
};

type PendingInvitation = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
};

/** Same title band as Regulations Library / Profile (Discovery 80px + 20px). */
const PAGE_TITLE_BAND_HEIGHT_PX = 100;

const UserManagementContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  minHeight: "100%",
  height: "auto",
  flex: "1 0 auto",
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  position: "relative",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
});

const HeaderSection = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  padding: "0px 64px 24px",
  height: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  display: "flex",
  alignItems: "flex-end",
  flexShrink: 0,
  boxSizing: "border-box",
  position: "sticky",
  top: 0,
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    padding: "0px 16px 16px",
    height: "auto",
    minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
    alignItems: "flex-end",
  },
}));

const HeaderContent = styled(Box)({
  display: "flex",
  gap: "8px",
  alignItems: "center",
});

const SettingsIconWrapper = styled(Box)({
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
});

const SettingsTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "28px",
  fontWeight: 500,
  lineHeight: "1.5",
  color: "#262626",
  letterSpacing: "-1px",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: "1 0 auto",
  padding: "16px 48px 24px",
  overflow: "visible",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "12px 16px 24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px 24px",
  },
}));

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const ContentArea = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  alignItems: "start",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    gap: "16px",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "12px",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  [theme.breakpoints.down("sm")]: {
    gap: "16px",
  },
}));

const UserManagementHeader = styled(Box)(({ theme }) => ({
  padding: "8px 4px",
  borderBottom: "1px solid #35d7e4",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "824px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
  },
}));

const UserManagementContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const UserManagementTitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "18px",
  fontWeight: 600,
  color: "#065b70",
  lineHeight: "normal",
});

const UserManagementSubtitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
  color: "#065b70",
  lineHeight: "normal",
});

const AddUserButton = styled(Button)(({ theme }) => ({
  backgroundColor: "transparent",
  color: "#065b70",
  border: "1px solid #bfe5ec",
  boxShadow: "none",
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "13px",
  fontWeight: 600,
  padding: "8px 14px",
  borderRadius: "8px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#e6fafc",
    border: "1px solid #5bbfd6",
    boxShadow: "none",
  },
  "&:disabled": {
    background: "#f5f5f5",
    color: "#9aa5a8",
    border: "1px solid #d9d9d9",
    cursor: "not-allowed",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "16px",
  alignItems: "start",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

const FacilityDropdown = styled(FormControl)(({ theme }) => ({
  width: "70%",
  "& .MuiOutlinedInput-root": {
    background: "linear-gradient(to bottom, #ffffff 95.192%, #effcff 53.846%)",
    borderRadius: "8px",
    border: "1px solid #bfe5ec",
    padding: "8px",
    "& .MuiSelect-select": {
      fontFamily: '"Rethink Sans", sans-serif',
      fontSize: "14px",
      padding: 0,
      color: "#262626",
      // Style for placeholder when no value selected
      "&[aria-expanded='false']": {
        color: "#737373",
      },
      // Style when value is selected
      "&:not([data-placeholder])": {
        color: "#262626",
      },
    },
    "& .MuiSelect-icon": {
      color: "#737373",
    },
    "&:hover": {
      border: "1px solid #bfe5ec",
    },
    "&.Mui-focused": {
      border: "1px solid #bfe5ec",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));


const UserListSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8fdfe",
  border: "1px solid #cff7ff",
  borderRadius: "8px",
  padding: "8px",
  width: "824px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const UserListHeader = styled(Box)({
  display: "flex",
  gap: "8px",
  alignItems: "start",
  marginBottom: "8px",
});

const UserListTitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "16px",
  fontWeight: 500,
  color: "#065b70",
  lineHeight: "normal",
  width: "376px",
});

const UserTable = styled(Box)({
  borderRadius: "4px",
  overflowX: "hidden",
  overflowY: "auto",
  border: "1px solid #0a829d",
  maxWidth: "100%",
});

const ADDED_USERS_GRID =
  "minmax(104px, 0.55fr) minmax(96px, 0.55fr) minmax(160px, 1.8fr) minmax(148px, max-content) 72px 72px";

const PENDING_INVITES_GRID =
  "minmax(104px, 0.55fr) minmax(96px, 0.55fr) minmax(160px, 1.8fr) minmax(148px, max-content) 72px";

const tableGridLayout = ($columns: string) => ({
  display: "grid",
  gridTemplateColumns: $columns,
  alignItems: "center",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box" as const,
});

const TableHeader = styled(Box)<{ $columns: string }>(({ $columns }) => ({
  ...tableGridLayout($columns),
  backgroundColor: "#0a829d",
  padding: "2px",
  minHeight: "32px",
}));

const TableHeaderCell = styled(Box)({
  padding: "2px 8px",
  color: "white",
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.2,
  minWidth: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
});

const TableBody = styled(Box)({
  borderBottom: "1px solid #e5f5f8",
  padding: "0 2px",
});

const TableRow = styled(Box)<{ $columns?: string }>(({ $columns }) => ({
  ...($columns ? tableGridLayout($columns) : { display: "block", width: "100%" }),
  padding: "8px 2px",
  borderBottom: "1px solid #e5f5f8",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const TableCell = styled(Box)<{ $email?: boolean; $span?: boolean }>(
  ({ $email, $span }) => ({
    padding: "2px 8px",
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "12px",
    color: "#262626",
    minWidth: 0,
    boxSizing: "border-box",
    ...($span
      ? { gridColumn: "1 / -1" }
      : $email
        ? {
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.3,
          }
        : {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }),
  }),
);

const ActionButton = styled(IconButton)({
  width: "36px",
  height: "28px",
  padding: "8px 10px",
  "& svg": {
    fontSize: "16px",
    color: "#262626",
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.6,
  },
});


export const UserManagement: React.FC = () => {
  const [selectedFacility, setSelectedFacility] = useState("");
  const [, setNpiNumber] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [rawUsers, setRawUsers] = useState<any[]>([]); // Store original user data with user_type_id
  const [loading, setLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [facilities, setFacilities] = useState<UserFacility[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState<boolean>(false);
  const [facilitiesError, setFacilitiesError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [pendingInvitations, setPendingInvitations] = useState<
    PendingInvitation[]
  >([]);
  const [pendingPage, setPendingPage] = useState<number>(1);
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState<number>(5);
  const [viewMode, setViewMode] = useState<"users" | "pending">("users");
  const [selectedCountry, setSelectedCountry] = useState<string>(
    getSelectedCountryCode(),
  );

  const isRestrictedFacility = (facility: any): boolean => {
    const raw =
      facility?.is_restricted ?? facility?.facility_details?.is_restricted;
    return (
      raw === true ||
      raw === 1 ||
      String(raw ?? "").trim().toLowerCase() === "true" ||
      String(raw ?? "").trim() === "1"
    );
  };
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [, setCurrentUserPermissions] = useState<
    UserPermission[]
  >([]);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [permissionsSuccess, setPermissionsSuccess] = useState<string | null>(
    null,
  );
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [features, setFeatures] = useState<any[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [permissionUserId, setPermissionUserId] = useState<string | null>(null);
  const [permissionFacilityId, setPermissionFacilityId] = useState<
    string | null
  >(null);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    userType: string;
    userTypeId?: number;
    userSubtypeId?: number;
    associationId?: number;
  } | null>(null);

  // Log page access to audit log
  useEffect(() => {
    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = getSelectedFacilityFromSession();
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: "/dashboard/usermanagement",
        method: "GET",
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: "User Management",
          action: "VIEW",
        },
        action_type: "VIEW",
        action_status: "SUCCESS",
      });
    };
    logPageAccess();
  }, []);

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        setLoadingFacilities(true);
        setFacilitiesError(null);
        const raw = localStorage.getItem("user");
        const parsed = raw
          ? (JSON.parse(raw) as Record<string, unknown>)
          : null;
        const userId =
          (parsed?.["id"] as number) || (parsed?.["user_id"] as number);
        if (!userId) {
          setFacilitiesError("Missing user id");
          return;
        }
        const res = await facilityManagementService.getUserFacilities(userId);
        const fetched = res.facilities || [];
        setFacilities(fetched);
        // Do not auto-select a facility. Keep placeholder shown by default.
        setSelectedFacility("");
        setNpiNumber("");
      } catch (e) {
        setFacilitiesError("Failed to load facilities");
      } finally {
        setLoadingFacilities(false);
      }
    };
    loadFacilities();
  }, [selectedCountry]);

  // Watch for country changes and refresh facilities when changed
  useEffect(() => {
    const checkCountryChange = () => {
      const current = getSelectedCountryCode();
      if (current !== selectedCountry) {
        setSelectedCountry(current);
      }
    };
    checkCountryChange();
    const intervalId = setInterval(checkCountryChange, 500);
    window.addEventListener("storage", checkCountryChange);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", checkCountryChange);
    };
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedFacility) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setUsersError(null);
      if (!selectedFacility) {
        setUsers([]);
        return;
      }
      const response = await getFacilityUsers(Number(selectedFacility));
      if (!response.success) {
        const msg =
          response.error?.message || response.message || "Failed to load users";
        setUsersError(msg);
      }
      console.log("users", response);
      const rawUsersData = response.users || [];
      setRawUsers(rawUsersData); // Store original user data
      const mapped: User[] = rawUsersData.map((u: any) => ({
        id:
          (u.id != null
            ? String(u.id)
            : u.user_id != null
              ? String(u.user_id)
              : "") || "",
        firstName: u.first_name || u.firstName || "",
        lastName: u.last_name || u.lastName || "",
        email: u.email || "",
        userType: u.user_type || u.userType || "",
        status:
          u.association_is_active !== undefined
            ? u.association_is_active
            : true,
      }));
      setUsers(mapped);
      const mappedPending: PendingInvitation[] = (
        response.pending_invitations || []
      ).map((p: any) => ({
        id: (p.id != null ? String(p.id) : "") || "",
        firstName: p.first_name || "",
        lastName: p.last_name || "",
        email: p.email || "",
        userType: p.user_type || "",
      }));
      setPendingInvitations(mappedPending);
      setPage(1);
    } catch (error) {
      console.error("Error loading users:", error);
      setUsers([]);
      setPendingInvitations([]);
      setUsersError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const totalItems = users.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUsers = users.slice(startIndex, endIndex);

  const pendingTotalItems = pendingInvitations.length;
  const pendingTotalPages = Math.max(
    1,
    Math.ceil(pendingTotalItems / pendingRowsPerPage),
  );
  const pendingCurrentPage = Math.min(pendingPage, pendingTotalPages);
  const pendingStartIndex = (pendingCurrentPage - 1) * pendingRowsPerPage;
  const pendingEndIndex = pendingStartIndex + pendingRowsPerPage;
  const paginatedPendingInvitations = pendingInvitations.slice(
    pendingStartIndex,
    pendingEndIndex,
  );

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (e: any) => {
    const value = Number(e.target.value);
    setRowsPerPage(value);
    setPage(1);
  };

  const handlePendingPageChange = (
    _: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPendingPage(value);
  };

  const handlePendingRowsPerPageChange = (e: any) => {
    const value = Number(e.target.value);
    setPendingRowsPerPage(value);
    setPendingPage(1);
  };

  const handleFacilityChange = (facilityId: string) => {
    setSelectedFacility(facilityId);
    const facility = facilities.find((f) => String(f.id) === facilityId);
    setNpiNumber(facility?.npi_number || "");
  };

  const handleAddUser = () => {
    setAddUserModalOpen(true);
  };

  const handleAddUserModalClose = () => {
    setAddUserModalOpen(false);
  };

  const handleUserAdded = async (_info?: {
    userId?: number;
    facilityId?: number;
    userTypeId?: number;
  }) => {
    // Refresh the user list after adding a new user; no permissions modal auto-open
    await loadUsers();
  };

  const handleUserAction = (
    event: React.MouseEvent<HTMLElement>,
    userId: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const handleActivateUser = async () => {
    if (!selectedUserId || !selectedFacility) {
      toast.error("Please select a user and facility.");
      handleCloseMenu();
      return;
    }

    // Find the user in rawUsers to get association_id
    const selectedUser = rawUsers.find(
      (u: any) => String(u.id || u.user_id || "") === selectedUserId,
    );

    if (!selectedUser) {
      toast.error("User not found.");
      handleCloseMenu();
      return;
    }

    const associationId =
      selectedUser.association_id || selectedUser.associationId;

    if (!associationId) {
      toast.error("Association ID not found for this user.");
      handleCloseMenu();
      return;
    }

    try {
      const baseUrl = getApiOrigin();
      if (!baseUrl) {
        throw new Error("API base URL is not configured.");
      }

      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Not authenticated. Please sign in again.");
      }

      const response = await fetch(
        `${baseUrl}/api/v1/facility-management/user-facility-association/${associationId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_active: true,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to activate user (${response.status})`,
        );
      }

      toast.success("User activated successfully.");
      handleCloseMenu();
      // Refresh the user list
      await loadUsers();
    } catch (error) {
      console.error("Error activating user:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to activate user.";
      toast.error(errorMessage);
    }
  };

  const handleDeactivateUser = async () => {
    if (!selectedUserId || !selectedFacility) {
      toast.error("Please select a user and facility.");
      handleCloseMenu();
      return;
    }

    // Find the user in rawUsers to get association_id
    const selectedUser = rawUsers.find(
      (u: any) => String(u.id || u.user_id || "") === selectedUserId,
    );

    if (!selectedUser) {
      toast.error("User not found.");
      handleCloseMenu();
      return;
    }

    const associationId =
      selectedUser.association_id || selectedUser.associationId;

    if (!associationId) {
      toast.error("Association ID not found for this user.");
      handleCloseMenu();
      return;
    }

    try {
      const baseUrl = getApiOrigin();
      if (!baseUrl) {
        throw new Error("API base URL is not configured.");
      }

      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Not authenticated. Please sign in again.");
      }

      const response = await fetch(
        `${baseUrl}/api/v1/facility-management/user-facility-association/${associationId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_active: false,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to deactivate user (${response.status})`,
        );
      }

      toast.success("User deactivated successfully.");
      handleCloseMenu();
      // Refresh the user list
      await loadUsers();
    } catch (error) {
      console.error("Error deactivating user:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to deactivate user.";
      toast.error(errorMessage);
    }
  };

  const handleRemoveUser = async () => {
    if (!selectedUserId || !selectedFacility) {
      toast.error("Please select a user and facility.");
      handleCloseMenu();
      return;
    }

    // Find the user in rawUsers to get association_id
    const selectedUser = rawUsers.find(
      (u: any) => String(u.id || u.user_id || "") === selectedUserId,
    );

    if (!selectedUser) {
      toast.error("User not found.");
      handleCloseMenu();
      return;
    }

    const associationId =
      selectedUser.association_id || selectedUser.associationId;

    if (!associationId) {
      toast.error("Association ID not found for this user.");
      handleCloseMenu();
      return;
    }

    try {
      const baseUrl = getApiOrigin();
      if (!baseUrl) {
        throw new Error("API base URL is not configured.");
      }

      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Not authenticated. Please sign in again.");
      }

      const response = await fetch(
        `${baseUrl}/api/v1/facility-management/user-facility-association/${associationId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_active: false,
            is_delete: true,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to remove user (${response.status})`,
        );
      }

      toast.success("User removed successfully.");
      handleCloseMenu();
      // Refresh the user list
      await loadUsers();
    } catch (error) {
      console.error("Error removing user:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to remove user.";
      toast.error(errorMessage);
    }
  };

  const buildPermissionMatrix = (
    featureList: any[],
    perms: UserPermission[],
  ): Record<
    string | number,
    { view: boolean; add: boolean; update: boolean; remove: boolean }
  > => {
    const matrix: Record<
      string | number,
      { view: boolean; add: boolean; update: boolean; remove: boolean }
    > = {};

    // Initialize all features with false (unchecked) - don't use default_permissions
    // Only show checked if permission is actually assigned
    featureList.forEach((f) => {
      matrix[f.id] = {
        view: false,
        add: false,
        update: false,
        remove: false,
      };
    });

    // Override with user-specific perms using feature_id direct match
    // Only set values for permissions that are actually assigned
    const safePerms = perms || [];
    console.log(
      "🔍 [UserManagement] Building matrix with permissions:",
      safePerms,
    );
    console.log("🔍 [UserManagement] Features list:", featureList);

    safePerms.forEach((perm) => {
      // Try multiple ways to get feature_id
      const fid = perm.feature_id ?? perm.id ?? (perm as any)?.feature?.id;

      if (fid == null) {
        console.warn(
          "⚠️ [UserManagement] Permission missing feature_id:",
          perm,
        );
        return;
      }

      // Convert to string for consistent key matching
      const fidKey = String(fid);

      // Initialize entry if it doesn't exist
      if (!matrix[fidKey] && !matrix[fid]) {
        matrix[fidKey] = {
          view: false,
          add: false,
          update: false,
          remove: false,
        };
      }

      const entry = matrix[fidKey] ||
        matrix[fid] || {
          view: false,
          add: false,
          update: false,
          remove: false,
        };
      // Only set values from actual assigned permissions
      entry.view = Boolean(perm.view);
      entry.add = Boolean(perm.add);
      entry.update = Boolean(perm.update);
      entry.remove = Boolean(perm.delete ?? (perm as any)?.remove);

      // Store under both string and number keys for compatibility
      matrix[fidKey] = entry;
      if (typeof fid === "number") {
        matrix[fid] = entry;
      }

      console.log(
        `✅ [UserManagement] Set permissions for feature ${fidKey}:`,
        entry,
      );
    });

    console.log("🔍 [UserManagement] Final permission matrix:", matrix);
    return matrix;
  };

  const [permissionMatrix, setPermissionMatrix] = useState<
    Record<
      string | number,
      { view: boolean; add: boolean; update: boolean; remove: boolean }
    >
  >({});

  const loadPermissionsData = async (
    userId: string,
    facilityId: string | null,
  ) => {
    setPermissionsLoading(true);
    setPermissionsError(null);
    setPermissionsSuccess(null);
    setFeaturesError(null);
    setFeaturesLoading(true);
    try {
      // Find the selected user's user_type_id from raw users data
      const selectedUser = rawUsers.find(
        (u: any) => String(u.id || u.user_id || "") === userId,
      );
      const userTypeId =
        selectedUser?.user_type_id || selectedUser?.userTypeId || null;
      console.log("🔍 [UserManagement] Selected user:", selectedUser);
      // Build filters object with user_type_id for the features API
      // Pass user_type_id in filters for the selected user
      const filters: Record<string, any> = {};
      if (userTypeId != null) {
        filters.user_type_id = userTypeId;
      }

      console.log("🔍 [UserManagement] Loading permissions for user:", {
        userId,
        facilityId,
        userTypeId,
        filters,
        selectedUser: selectedUser
          ? {
              id: selectedUser.id || selectedUser.user_id,
              user_type_id: userTypeId,
            }
          : null,
      });

      // If no facility is selected, only load features (not permissions)
      if (!facilityId) {
        const featuresResp = await fetchFeatures({
          page: 1,
          pageSize: 200,
          filters,
        });
        const feats =
          (featuresResp as any)?.result?.data ||
          (featuresResp as any)?.results ||
          (featuresResp as any)?.items ||
          [];
        setCurrentUserPermissions([]);
        setFeatures(feats);
        setPermissionMatrix(buildPermissionMatrix(feats, []));
        setPermissionsModalOpen(true);
      } else {
        // Load both permissions and features when facility is selected
        const [permsResp, featuresResp] = await Promise.all([
          fetchUserPermissions(userId, facilityId),
          fetchFeatures({ page: 1, pageSize: 200, filters }),
        ]);

        // Handle different API response structures
        let perms: any[] = [];
        if (Array.isArray(permsResp)) {
          perms = permsResp;
        } else if (
          (permsResp as any)?.permissions &&
          Array.isArray((permsResp as any).permissions)
        ) {
          perms = (permsResp as any).permissions;
        } else if (
          (permsResp as any)?.result?.data &&
          Array.isArray((permsResp as any).result.data)
        ) {
          perms = (permsResp as any).result.data;
        } else if (
          (permsResp as any)?.items &&
          Array.isArray((permsResp as any).items)
        ) {
          perms = (permsResp as any).items;
        } else if (
          (permsResp as any)?.results &&
          Array.isArray((permsResp as any).results)
        ) {
          perms = (permsResp as any).results;
        } else if (
          (permsResp as any)?.data &&
          Array.isArray((permsResp as any).data)
        ) {
          perms = (permsResp as any).data;
        }

        const feats =
          (featuresResp as any)?.result?.data ||
          (featuresResp as any)?.results ||
          (featuresResp as any)?.items ||
          [];

        console.log("🔍 [UserManagement] Loaded permissions:", perms);
        console.log("🔍 [UserManagement] Loaded features:", feats);
        console.log(
          "🔍 [UserManagement] Permissions response structure:",
          permsResp,
        );

        setCurrentUserPermissions(perms as UserPermission[]);
        setFeatures(feats);
        const matrix = buildPermissionMatrix(feats, perms as UserPermission[]);
        console.log("🔍 [UserManagement] Built permission matrix:", matrix);
        setPermissionMatrix(matrix);
        setPermissionsModalOpen(true);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load permissions data.";
      setPermissionsError(message);
    } finally {
      setPermissionsLoading(false);
      setFeaturesLoading(false);
    }
  };

  const handleEditPermissions = async () => {
    if (!selectedUserId) {
      handleCloseMenu();
      return;
    }
    setPermissionUserId(selectedUserId);
    setPermissionFacilityId(selectedFacility || null);
    await loadPermissionsData(selectedUserId, selectedFacility || null);
    handleCloseMenu();
  };

  const handleEditUser = () => {
    if (!selectedUserId) {
      handleCloseMenu();
      return;
    }

    // Find the user in rawUsers to get all user data
    const selectedUser = rawUsers.find(
      (u: any) => String(u.id || u.user_id || "") === selectedUserId,
    );

    if (!selectedUser) {
      toast.error("User not found.");
      handleCloseMenu();
      return;
    }

    // Map the user data to the format expected by EditUserModal
    const userData = {
      id: selectedUserId,
      firstName: selectedUser.first_name || selectedUser.firstName || "",
      lastName: selectedUser.last_name || selectedUser.lastName || "",
      email: selectedUser.email || "",
      userType: selectedUser.user_type || selectedUser.userType || "",
      userTypeId: selectedUser.user_type_id || selectedUser.userTypeId,
      userSubtypeId: selectedUser.user_subtype_id || selectedUser.userSubtypeId,
      associationId: selectedUser.association_id || selectedUser.associationId,
    };

    setUserToEdit(userData);
    setEditUserModalOpen(true);
    handleCloseMenu();
  };

  const handleEditUserModalClose = () => {
    setEditUserModalOpen(false);
    setUserToEdit(null);
  };

  const handleUserUpdated = async () => {
    // Refresh the user list after updating
    await loadUsers();
  };

  const togglePermission = (
    featureId: string | number,
    field: "view" | "add" | "update" | "remove",
  ) => {
    setPermissionMatrix((prev) => {
      const current = prev[featureId] || {
        view: false,
        add: false,
        update: false,
        remove: false,
      };
      const newValue = !current[field];

      // If unchecking view, automatically uncheck all other permissions
      if (field === "view" && newValue === false) {
        return {
          ...prev,
          [featureId]: {
            view: false,
            add: false,
            update: false,
            remove: false,
          },
        };
      }

      // If checking add, update, or remove, and view is currently unchecked, auto-check view
      const shouldAutoCheckView =
        (field === "add" || field === "update" || field === "remove") &&
        newValue === true &&
        current.view === false;

      return {
        ...prev,
        [featureId]: {
          ...current,
          [field]: newValue,
          ...(shouldAutoCheckView && { view: true }),
        },
      };
    });
  };

  const toggleAllPermissions = (
    field: "view" | "add" | "update" | "remove",
    value: boolean,
    levelFeatures: any[],
  ) => {
    setPermissionMatrix((prev) => {
      const next = { ...prev };
      levelFeatures.forEach((f) => {
        // Check if this checkbox is disabled based on default_permissions
        const isDisabled = (() => {
          if (field === "view") return !Boolean(f?.default_permissions?.view);
          if (field === "add") return !Boolean(f?.default_permissions?.add);
          if (field === "update")
            return !Boolean(f?.default_permissions?.update);
          if (field === "remove")
            return !Boolean(f?.default_permissions?.delete);
          return false;
        })();

        // Skip disabled checkboxes - don't change their state
        if (isDisabled) {
          return;
        }

        const current = prev[f.id] || {
          view: false,
          add: false,
          update: false,
          remove: false,
        };

        // If unchecking view, automatically uncheck all other permissions (only for enabled checkboxes)
        if (field === "view" && value === false) {
          next[f.id] = {
            view: false,
            add: false,
            update: false,
            remove: false,
          };
          return;
        }

        // If checking add, update, or remove, and view is currently unchecked, auto-check view
        // But only if view is not disabled
        const shouldAutoCheckView =
          (field === "add" || field === "update" || field === "remove") &&
          value === true &&
          current.view === false &&
          Boolean(f?.default_permissions?.view); // Only auto-check view if it's not disabled

        next[f.id] = {
          ...current,
          [field]: value,
          ...(shouldAutoCheckView && { view: true }),
        };
      });
      return next;
    });
  };

  // Calculate checked states for a specific level
  // Only considers enabled checkboxes (those with default_permissions set)
  const getLevelCheckedStates = (levelFeatures: any[]) => {
    // Filter to only enabled features for each permission type
    const enabledViewFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.view),
    );
    const enabledAddFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.add),
    );
    const enabledUpdateFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.update),
    );
    const enabledRemoveFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.delete),
    );

    const allViewChecked =
      enabledViewFeatures.length > 0 &&
      enabledViewFeatures.every((f) => permissionMatrix[f.id]?.view);
    const someViewChecked = enabledViewFeatures.some(
      (f) => permissionMatrix[f.id]?.view,
    );

    const allAddChecked =
      enabledAddFeatures.length > 0 &&
      enabledAddFeatures.every((f) => permissionMatrix[f.id]?.add);
    const someAddChecked = enabledAddFeatures.some(
      (f) => permissionMatrix[f.id]?.add,
    );

    const allUpdateChecked =
      enabledUpdateFeatures.length > 0 &&
      enabledUpdateFeatures.every((f) => permissionMatrix[f.id]?.update);
    const someUpdateChecked = enabledUpdateFeatures.some(
      (f) => permissionMatrix[f.id]?.update,
    );

    const allRemoveChecked =
      enabledRemoveFeatures.length > 0 &&
      enabledRemoveFeatures.every((f) => permissionMatrix[f.id]?.remove);
    const someRemoveChecked = enabledRemoveFeatures.some(
      (f) => permissionMatrix[f.id]?.remove,
    );

    return {
      allViewChecked,
      someViewChecked,
      allAddChecked,
      someAddChecked,
      allUpdateChecked,
      someUpdateChecked,
      allRemoveChecked,
      someRemoveChecked,
    };
  };

  const handleClosePermissionsModal = () => {
    setPermissionsModalOpen(false);
  };

  const handleSavePermissions = async () => {
    const userIdToUse = permissionUserId || selectedUserId;
    const facilityIdToUse = permissionFacilityId || selectedFacility || null;

    if (!userIdToUse) {
      setPermissionsError("Please select a user.");
      return;
    }

    const featuresPayload = Object.entries(permissionMatrix).map(
      ([featureId, perms]) => ({
        feature_id: featureId,
        view: perms.view,
        add: perms.add,
        update: perms.update,
        delete: perms.remove,
      }),
    );

    setPermissionsSaving(true);
    setPermissionsError(null);
    setPermissionsSuccess(null);
    try {
      const payload: any = {
        user_id: userIdToUse,
        features: featuresPayload,
      };
      // Only include facility_id if it's provided
      if (facilityIdToUse) {
        payload.facility_id = facilityIdToUse;
      }

      await saveUserPermissions(payload);

      // If we reach here, the API call was successful (status 200)
      // This includes cases where permissions already exist or are upserted
      // Always show a consistent success message
      const successMessage = "Permissions saved successfully.";

      // Show success toast
      toast.success(successMessage);

      // Close the modal on successful save (including when permissions already exist)
      setPermissionsModalOpen(false);

      // Don't reload permissions data here as it would reopen the modal
      // Permissions will be refreshed when user opens the modal again
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to save permissions.";
      setPermissionsError(message);
      // Modal stays open on error - no need to close it
    } finally {
      setPermissionsSaving(false);
    }
  };

  return (
    <UserManagementContainer>
      <HeaderSection
        style={{
          height: PAGE_TITLE_BAND_HEIGHT_PX,
          minHeight: PAGE_TITLE_BAND_HEIGHT_PX,
          padding: "0 64px 24px",
          display: "flex",
          alignItems: "flex-end",
          boxSizing: "border-box",
        }}
      >
        <HeaderContent>
          <SettingsIconWrapper>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5C13.3488 16.5 14.559 15.9066 15.3838 14.9666C16.0787 14.1745 16.5 13.1365 16.5 12C16.5 11.5401 16.431 11.0962 16.3028 10.6783C15.7382 8.838 14.0253 7.5 12 7.5ZM9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12ZM19.7093 20.3947L17.9818 19.6364C17.4876 19.4197 16.9071 19.4514 16.44 19.7219C15.9729 19.9923 15.675 20.4692 15.6157 21.0065L15.408 22.8855C15.3651 23.2729 15.084 23.5917 14.7055 23.6819C12.9263 24.106 11.0725 24.106 9.29326 23.6819C8.91476 23.5917 8.63363 23.2729 8.59081 22.8855L8.38343 21.0093C8.32251 20.473 8.0112 19.9976 7.54452 19.728C7.07783 19.4585 6.51117 19.4269 6.01859 19.6424L4.29071 20.4009C3.93281 20.558 3.51493 20.4718 3.24806 20.1858C2.00474 18.8536 1.07924 17.2561 0.541215 15.5136C0.425331 15.1383 0.559224 14.7307 0.8749 14.4976L2.40219 13.3703C2.83721 13.05 3.09414 12.5415 3.09414 12.0006C3.09414 11.4597 2.83721 10.9512 2.40162 10.6305L0.875288 9.50507C0.559144 9.27196 0.425127 8.86375 0.54142 8.48812C1.08038 6.74728 2.00637 5.15157 3.24971 3.82108C3.51684 3.53522 3.93492 3.44935 4.29276 3.60685L6.01296 4.36398C6.50793 4.58162 7.07696 4.54875 7.54617 4.27409C8.01335 4.00258 8.32437 3.52521 8.38442 2.98787L8.59334 1.11011C8.63697 0.717971 8.92453 0.396974 9.30894 0.311314C10.19 0.114979 11.0891 0.0106508 12.0131 0C12.9147 0.0104117 13.8128 0.114784 14.6928 0.311432C15.077 0.397275 15.3643 0.718169 15.4079 1.11011L15.617 2.98931C15.7116 3.85214 16.4387 4.50566 17.3055 4.50657C17.5385 4.50694 17.769 4.45832 17.9843 4.36288L19.7048 3.60562C20.0626 3.44812 20.4807 3.53399 20.7478 3.81984C21.9912 5.15034 22.9172 6.74605 23.4561 8.48689C23.5723 8.86227 23.4386 9.27022 23.1228 9.50341L21.5978 10.6297C21.1628 10.9499 20.9 11.4585 20.9 11.9994C20.9 12.5402 21.1628 13.0488 21.5988 13.3697L23.1251 14.4964C23.441 14.7296 23.5748 15.1376 23.4586 15.513C22.9198 17.2536 21.9944 18.8491 20.7517 20.1799C20.4849 20.4657 20.0671 20.5518 19.7093 20.3947ZM14.263 20.1965C14.4982 19.4684 14.9889 18.8288 15.6884 18.4238C16.5702 17.9132 17.6536 17.8546 18.5841 18.2626L19.9281 18.8526C20.791 17.8537 21.4593 16.7013 21.8981 15.4551L20.7095 14.5777L20.7086 14.577C19.898 13.9799 19.4 13.0276 19.4 11.9994C19.4 10.9718 19.8974 10.0195 20.7073 9.42265L20.7085 9.4217L21.8957 8.54496C21.4567 7.29874 20.7881 6.1463 19.9248 5.14764L18.5922 5.73419L18.5899 5.73521C18.1844 5.91457 17.7472 6.00716 17.3039 6.00657C15.6715 6.00447 14.3046 4.77425 14.1261 3.15459L14.1259 3.15285L13.9635 1.69298C13.3202 1.57322 12.6677 1.50866 12.013 1.50011C11.3389 1.50885 10.6821 1.57361 10.0377 1.69322L9.87514 3.15446C9.76248 4.16266 9.17931 5.05902 8.30191 5.5698C7.41937 6.08554 6.34453 6.14837 5.40869 5.73688L4.07273 5.14887C3.20949 6.14745 2.54092 7.29977 2.10196 8.54587L3.29181 9.42319C4.11115 10.0268 4.59414 10.9836 4.59414 12.0006C4.59414 13.0172 4.11142 13.9742 3.29237 14.5776L2.10161 15.4565C2.54002 16.7044 3.2085 17.8584 4.07205 18.8587L5.41742 18.2681C6.34745 17.8613 7.41573 17.9214 8.29471 18.4291C9.17398 18.9369 9.75934 19.8319 9.87384 20.84L9.87435 20.8445L10.0362 22.3087C11.3326 22.5638 12.6662 22.5638 13.9626 22.3087L14.1247 20.8417C14.1491 20.6217 14.1955 20.4054 14.263 20.1965Z"
                fill="#065B70"
              />
            </svg>
          </SettingsIconWrapper>
          <SettingsTitle>Settings</SettingsTitle>
        </HeaderContent>
      </HeaderSection>

      <ContentSection>
        <ContentWrapper>
          <ContentArea>
            <FormSection>
              <UserManagementHeader>
                <UserManagementContent>
                  <UserManagementTitle>User Management</UserManagementTitle>
                  <UserManagementSubtitle>
                    Please Provide your facility details to personalize your
                    compliance experience
                  </UserManagementSubtitle>
                </UserManagementContent>
                {canAddByName(FEATURE_NAMES.USER_MANAGEMENT) && (
                  <AddUserButton onClick={handleAddUser}>
                    Add User
                  </AddUserButton>
                )}
              </UserManagementHeader>

              <FormRow>
                <FacilityDropdown>
                  <Select
                    value={selectedFacility}
                    onChange={(e) => handleFacilityChange(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#737373" }}>
                            {loadingFacilities
                              ? "Loading facilities..."
                              : facilitiesError || "Select a Facility"}
                          </span>
                        );
                      }
                      const facility = facilities.find(
                        (f) => String(f.id) === selected,
                      );
                      return (
                        <span style={{ color: "#262626" }}>
                          {facility
                            ? formatFacilityDropdownLabel({
                                facility_name:
                                  facility.facility_name ||
                                  `Facility ${facility.id}`,
                                city: facility.city,
                                state: facility.state,
                                address_1: facility.address_1,
                                address_2: facility.address_2,
                                full_address: facility.full_address,
                                country_code: facility.country_code,
                                ccn_number:
                                  facility.npi_number ??
                                  (facility as { ccn_number?: string })
                                    .ccn_number,
                              })
                            : ""}
                        </span>
                      );
                    }}
                  >
                    <MenuItem value="" disabled>
                      {loadingFacilities
                        ? "Loading facilities..."
                        : facilitiesError || "Select a Facility"}
                    </MenuItem>
                    {facilities.map((facility) => (
                      <MenuItem
                        key={facility.id}
                        value={String(facility.id)}
                        disabled={isRestrictedFacility(facility)}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1,
                          }}
                        >
                          <span>
                            {formatFacilityDropdownLabel({
                              facility_name:
                                facility.facility_name ||
                                `Facility ${facility.id}`,
                              city: facility.city,
                              state: facility.state,
                              address_1: facility.address_1,
                              address_2: facility.address_2,
                              full_address: facility.full_address,
                              country_code: facility.country_code,
                              ccn_number:
                                facility.npi_number ??
                                (facility as { ccn_number?: string })
                                  .ccn_number,
                            })}
                          </span>
                          {isRestrictedFacility(facility) && (
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#DC2626",
                                flexShrink: 0,
                              }}
                            >
                              Restricted
                            </Typography>
                          )}
                          {facility.is_affable_connected && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <AffableConnectedIcon />
                            </Box>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FacilityDropdown>

                {/* <NPISection>
                  <NPILabel>
                    <NPILabelText>NPI Number</NPILabelText>
                  </NPILabel>
                  <NPIInput
                    value={npiNumber}
                    onChange={(e) => setNpiNumber(e.target.value)}
                    variant="outlined"
                    disabled
                  />
                </NPISection> */}
              </FormRow>

              {/* View Toggle (below facility dropdown) */}
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  variant={viewMode === "users" ? "contained" : "outlined"}
                  onClick={() => setViewMode("users")}
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    textTransform: "none",
                    backgroundColor:
                      viewMode === "users" ? "#0a829d" : undefined,
                    color: viewMode === "users" ? "#ffffff" : "#065b70",
                    borderColor: "#0a829d",
                    "&:hover": {
                      backgroundColor:
                        viewMode === "users" ? "#0a829d" : undefined,
                    },
                  }}
                >
                  Added Users
                </Button>
                <Button
                  variant={viewMode === "pending" ? "contained" : "outlined"}
                  onClick={() => setViewMode("pending")}
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    textTransform: "none",
                    backgroundColor:
                      viewMode === "pending" ? "#0a829d" : undefined,
                    color: viewMode === "pending" ? "#ffffff" : "#065b70",
                    borderColor: "#0a829d",
                    "&:hover": {
                      backgroundColor:
                        viewMode === "pending" ? "#0a829d" : undefined,
                    },
                  }}
                >
                  Pending Invitations
                </Button>
              </Box>

              {viewMode === "users" && (
                <UserListSection>
                  <UserListHeader>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z"
                        fill="#065b70"
                      />
                      <path
                        d="M3 18C3 14.134 6.13401 11 10 11C13.866 11 17 14.134 17 18H3Z"
                        fill="#065b70"
                      />
                    </svg>
                    <UserListTitle>Users Added to the facility</UserListTitle>
                  </UserListHeader>

                  <UserTable>
                    <TableHeader $columns={ADDED_USERS_GRID}>
                      <TableHeaderCell>First Name</TableHeaderCell>
                      <TableHeaderCell>Last Name</TableHeaderCell>
                      <TableHeaderCell>Email</TableHeaderCell>
                      <TableHeaderCell>User Type</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Action</TableHeaderCell>
                    </TableHeader>

                    <TableBody>
                      {!loading && usersError && users.length === 0 && (
                        <Box sx={{ p: 2 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              fontSize: 12,
                              fontWeight: 400,
                              color: "#c62828",
                              lineHeight: "normal",
                            }}
                          >
                            {usersError}
                          </Typography>
                        </Box>
                      )}
                      {paginatedUsers.map((user) => (
                        <TableRow key={user.id} $columns={ADDED_USERS_GRID}>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell $email>{user.email}</TableCell>
                          <TableCell>
                            {formatUserTypeDisplayName(user.userType)}
                          </TableCell>
                          <TableCell>
                            {user.status === false ? "Inactive" : "Active"}
                          </TableCell>
                          <TableCell>
                            <ActionButton
                              onClick={(e) => handleUserAction(e, user.id)}
                              disabled={
                                !canUpdateByName(
                                  FEATURE_NAMES.USER_MANAGEMENT,
                                ) &&
                                !canDeleteByName(FEATURE_NAMES.USER_MANAGEMENT)
                              }
                            >
                              <svg
                                width="16"
                                height="4"
                                viewBox="0 0 16 4"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle
                                  cx="2"
                                  cy="2"
                                  r="2"
                                  fill={
                                    !canUpdateByName(
                                      FEATURE_NAMES.USER_MANAGEMENT,
                                    ) &&
                                    !canDeleteByName(
                                      FEATURE_NAMES.USER_MANAGEMENT,
                                    )
                                      ? "#BDBDBD"
                                      : "#262626"
                                  }
                                />
                                <circle
                                  cx="8"
                                  cy="2"
                                  r="2"
                                  fill={
                                    !canUpdateByName(
                                      FEATURE_NAMES.USER_MANAGEMENT,
                                    ) &&
                                    !canDeleteByName(
                                      FEATURE_NAMES.USER_MANAGEMENT,
                                    )
                                      ? "#BDBDBD"
                                      : "#262626"
                                  }
                                />
                                <circle
                                  cx="14"
                                  cy="2"
                                  r="2"
                                  fill={
                                    !canUpdateByName(
                                      FEATURE_NAMES.USER_MANAGEMENT,
                                    ) &&
                                    !canDeleteByName(
                                      FEATURE_NAMES.USER_MANAGEMENT,
                                    )
                                      ? "#BDBDBD"
                                      : "#262626"
                                  }
                                />
                              </svg>
                            </ActionButton>
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Pagination row inside table border */}
                      <TableRow $columns={ADDED_USERS_GRID}>
                        <TableCell $span>
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              flexWrap: "wrap",
                              py: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontSize: 12,
                                color: "#065b70",
                              }}
                            >
                              Showing {totalItems === 0 ? 0 : startIndex + 1}-
                              {Math.min(endIndex, totalItems)} of {totalItems}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Rethink Sans, sans-serif",
                                  fontSize: 12,
                                  color: "#065b70",
                                }}
                              >
                                Rows per page
                              </Typography>
                              <Select
                                size="small"
                                value={rowsPerPage}
                                onChange={handleRowsPerPageChange}
                                sx={{
                                  minWidth: 80,
                                  width: 80,
                                  height: 28,
                                  "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                  },
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      "& .MuiMenuItem-root": {
                                        fontSize: 12,
                                        minHeight: "28px",
                                        lineHeight: "28px",
                                        fontFamily:
                                          '"Rethink Sans", sans-serif',
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                              </Select>
                              <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="small"
                                sx={{
                                  "& .MuiPaginationItem-root": {
                                    fontSize: 12,
                                    height: 28,
                                    minWidth: 28,
                                    padding: "0 6px",
                                  },
                                  "& .MuiPaginationItem-root.Mui-selected": {
                                    backgroundColor: "#0a829d",
                                    color: "#ffffff",
                                  },
                                  "& .MuiPaginationItem-root.Mui-selected:hover":
                                    { backgroundColor: "#0a829d" },
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </UserTable>
                </UserListSection>
              )}

              {viewMode === "pending" && (
                <UserListSection>
                  <UserListHeader>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z"
                        fill="#065b70"
                      />
                      <path
                        d="M3 18C3 14.134 6.13401 11 10 11C13.866 11 17 14.134 17 18H3Z"
                        fill="#065b70"
                      />
                    </svg>
                    <UserListTitle>Pending Invitations</UserListTitle>
                  </UserListHeader>

                  <UserTable>
                    <TableHeader $columns={PENDING_INVITES_GRID}>
                      <TableHeaderCell>First Name</TableHeaderCell>
                      <TableHeaderCell>Last Name</TableHeaderCell>
                      <TableHeaderCell>Email</TableHeaderCell>
                      <TableHeaderCell>User Type</TableHeaderCell>
                      <TableHeaderCell>Action</TableHeaderCell>
                    </TableHeader>

                    <TableBody>
                      {pendingInvitations.length === 0 && (
                        <Box sx={{ p: 2 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              fontSize: 12,
                              fontWeight: 400,
                              color: "#737373",
                              lineHeight: "normal",
                            }}
                          >
                            No pending invitations
                          </Typography>
                        </Box>
                      )}
                      {paginatedPendingInvitations.map((inv) => (
                        <TableRow key={inv.id} $columns={PENDING_INVITES_GRID}>
                          <TableCell>{inv.firstName}</TableCell>
                          <TableCell>{inv.lastName}</TableCell>
                          <TableCell $email>{inv.email}</TableCell>
                          <TableCell>
                            {formatUserTypeDisplayName(inv.userType)}
                          </TableCell>
                          <TableCell>
                            <ActionButton disabled>
                              <svg
                                width="16"
                                height="4"
                                viewBox="0 0 16 4"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle cx="2" cy="2" r="2" fill="#BDBDBD" />
                                <circle cx="8" cy="2" r="2" fill="#BDBDBD" />
                                <circle cx="14" cy="2" r="2" fill="#BDBDBD" />
                              </svg>
                            </ActionButton>
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Pagination row inside table border */}
                      <TableRow $columns={PENDING_INVITES_GRID}>
                        <TableCell $span>
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              flexWrap: "wrap",
                              py: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontSize: 12,
                                color: "#065b70",
                              }}
                            >
                              Showing{" "}
                              {pendingTotalItems === 0
                                ? 0
                                : pendingStartIndex + 1}
                              -{Math.min(pendingEndIndex, pendingTotalItems)} of{" "}
                              {pendingTotalItems}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Rethink Sans, sans-serif",
                                  fontSize: 12,
                                  color: "#065b70",
                                }}
                              >
                                Rows per page
                              </Typography>
                              <Select
                                size="small"
                                value={pendingRowsPerPage}
                                onChange={handlePendingRowsPerPageChange}
                                sx={{
                                  minWidth: 80,
                                  width: 80,
                                  height: 28,
                                  "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                  },
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      "& .MuiMenuItem-root": {
                                        fontSize: 12,
                                        minHeight: "28px",
                                        lineHeight: "28px",
                                        fontFamily:
                                          '"Rethink Sans", sans-serif',
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                              </Select>
                              <Pagination
                                count={pendingTotalPages}
                                page={pendingCurrentPage}
                                onChange={handlePendingPageChange}
                                color="primary"
                                size="small"
                                sx={{
                                  "& .MuiPaginationItem-root": {
                                    fontSize: 12,
                                    height: 28,
                                    minWidth: 28,
                                    padding: "0 6px",
                                  },
                                  "& .MuiPaginationItem-root.Mui-selected": {
                                    backgroundColor: "#0a829d",
                                    color: "#ffffff",
                                  },
                                  "& .MuiPaginationItem-root.Mui-selected:hover":
                                    { backgroundColor: "#0a829d" },
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </UserTable>
                </UserListSection>
              )}
            </FormSection>
          </ContentArea>
        </ContentWrapper>
      </ContentSection>

      {/* <CloseButton onClick={handleClose}>
        <svg
          width="14"
          height="8"
          viewBox="0 0 14 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ transform: "rotate(90deg)" }}
        >
          <path
            d="M1 1L7 7L13 1"
            stroke="#065B70"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </CloseButton> */}

      {/* User Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {canUpdateByName(FEATURE_NAMES.USER_MANAGEMENT) && (
          <>
            <MenuItem
              onClick={handleEditUser}
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 400,
                color: "#262626",
                lineHeight: "normal",
              }}
            >
              Edit User
            </MenuItem>
            <MenuItem
              onClick={handleEditPermissions}
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 400,
                color: "#262626",
                lineHeight: "normal",
              }}
            >
              Edit Permissions
            </MenuItem>
            {(() => {
              // Get the selected user's status
              const selectedUser = rawUsers.find(
                (u: any) => String(u.id || u.user_id || "") === selectedUserId,
              );
              const isActive = selectedUser?.association_is_active !== false;

              return isActive ? (
                <MenuItem
                  onClick={handleDeactivateUser}
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#262626",
                    lineHeight: "normal",
                  }}
                >
                  Deactivate User
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={handleActivateUser}
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#262626",
                    lineHeight: "normal",
                  }}
                >
                  Activate User
                </MenuItem>
              );
            })()}
          </>
        )}
        {canDeleteByName(FEATURE_NAMES.USER_MANAGEMENT) && (
          <MenuItem
            onClick={handleRemoveUser}
            sx={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "14px",
              fontWeight: 400,
              color: "#262626",
              lineHeight: "normal",
            }}
          >
            Remove User
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={permissionsModalOpen}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClosePermissionsModal();
        }}
        fullWidth
        maxWidth="md"
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "18px",
              fontWeight: 600,
              color: "#065b70",
            }}
          >
            Edit Permissions
          </Typography>
          <IconButton
            onClick={handleClosePermissionsModal}
            sx={{
              padding: "4px",
              color: "#737373",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                color: "#065b70",
              },
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: "60vh",
            overflow: "auto",
            position: "relative",
            pt: 3,
            px: 3,
            pb: 2,
          }}
        >
          {permissionsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {permissionsError}
            </Alert>
          )}
          {permissionsSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {permissionsSuccess}
            </Alert>
          )}
          {featuresError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {featuresError}
            </Alert>
          )}
          {!permissionFacilityId && !selectedFacility && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No facility selected. Permissions will be saved without facility
              association. Select a facility to view existing permissions.
            </Alert>
          )}
          {(permissionsLoading || featuresLoading) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <CircularProgress size={20} sx={{ color: "#065b70" }} />
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: 14,
                  color: "#065b70",
                }}
              >
                Loading permissions...
              </Typography>
            </Box>
          )}
          {!permissionsLoading &&
            !featuresLoading &&
            (() => {
              // Group features by feature_level
              const permissionEditorFeatures =
                filterFeaturesForPermissionEditor(features);
              const facilityFeatures = permissionEditorFeatures.filter(
                (f) => f.feature_level === 1,
              );
              const userFeatures = permissionEditorFeatures.filter(
                (f) => f.feature_level === 2,
              );

              return (
                <Box sx={{ overflowX: "auto" }}>
                  {/* User Level Features */}
                  {userFeatures.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#0a829d",
                          textDecoration: "underline",
                          textUnderlineOffset: "4px",
                          mb: 2,
                        }}
                      >
                        User Level Features (All Facilities)
                      </Typography>
                      <MUITable
                        size="small"
                        sx={{ tableLayout: "fixed", width: "100%" }}
                      >
                        <colgroup>
                          <col style={{ width: "40%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                        </colgroup>
                        <MUITableHead>
                          <MUITableRow>
                            <MUITableCell
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              Feature
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  View
                                </Typography>
                                {(() => {
                                  const userLevelStates =
                                    getLevelCheckedStates(userFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={userLevelStates.allViewChecked}
                                      indeterminate={
                                        !userLevelStates.allViewChecked &&
                                        userLevelStates.someViewChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "view",
                                          e.target.checked,
                                          userFeatures,
                                        )
                                      }
                                      disabled={
                                        userFeatures.length > 0 &&
                                        userFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.view,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  Add
                                </Typography>
                                {(() => {
                                  const userLevelStates =
                                    getLevelCheckedStates(userFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={userLevelStates.allAddChecked}
                                      indeterminate={
                                        !userLevelStates.allAddChecked &&
                                        userLevelStates.someAddChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "add",
                                          e.target.checked,
                                          userFeatures,
                                        )
                                      }
                                      disabled={
                                        userFeatures.length > 0 &&
                                        userFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.add,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  Edit
                                </Typography>
                                {(() => {
                                  const userLevelStates =
                                    getLevelCheckedStates(userFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={userLevelStates.allUpdateChecked}
                                      indeterminate={
                                        !userLevelStates.allUpdateChecked &&
                                        userLevelStates.someUpdateChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "update",
                                          e.target.checked,
                                          userFeatures,
                                        )
                                      }
                                      disabled={
                                        userFeatures.length > 0 &&
                                        userFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.update,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  Delete
                                </Typography>
                                {(() => {
                                  const userLevelStates =
                                    getLevelCheckedStates(userFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={userLevelStates.allRemoveChecked}
                                      indeterminate={
                                        !userLevelStates.allRemoveChecked &&
                                        userLevelStates.someRemoveChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "remove",
                                          e.target.checked,
                                          userFeatures,
                                        )
                                      }
                                      disabled={
                                        userFeatures.length > 0 &&
                                        userFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.delete,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                          </MUITableRow>
                        </MUITableHead>
                        <MUITableBody>
                          {userFeatures.map((feature) => {
                            // Try both string and number keys for feature ID
                            const featureIdKey = String(feature.id);
                            const featureIdNum = Number(feature.id);
                            const state = permissionMatrix[featureIdKey] ||
                              permissionMatrix[featureIdNum] ||
                              permissionMatrix[feature.id] || {
                                view: false,
                                add: false,
                                update: false,
                                remove: false,
                              };
                            return (
                              <MUITableRow key={feature.id}>
                                <MUITableCell
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: "13px",
                                    color: "#262626",
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  {getFeatureDisplayName(
                                    feature.feature_name ||
                                      feature.name ||
                                      String(feature.code ?? feature.id ?? ""),
                                  )}
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.view,
                                      ) && state.view
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "view")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.view,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.add,
                                      ) && state.add
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "add")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.add,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.update,
                                      ) && state.update
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "update")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.update,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.delete,
                                      ) && state.remove
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "remove")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.delete,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                              </MUITableRow>
                            );
                          })}
                        </MUITableBody>
                      </MUITable>
                    </Box>
                  )}

                  {/* Facility Level Features */}
                  {facilityFeatures.length > 0 && (
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#0a829d",
                          textDecoration: "underline",
                          textUnderlineOffset: "4px",
                          mb: 2,
                        }}
                      >
                        Facility Level Features
                      </Typography>
                      <MUITable
                        size="small"
                        sx={{ tableLayout: "fixed", width: "100%" }}
                      >
                        <colgroup>
                          <col style={{ width: "40%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                        </colgroup>
                        <MUITableHead>
                          <MUITableRow>
                            <MUITableCell
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              Feature
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  View
                                </Typography>
                                {(() => {
                                  const facilityLevelStates =
                                    getLevelCheckedStates(facilityFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={
                                        facilityLevelStates.allViewChecked
                                      }
                                      indeterminate={
                                        !facilityLevelStates.allViewChecked &&
                                        facilityLevelStates.someViewChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "view",
                                          e.target.checked,
                                          facilityFeatures,
                                        )
                                      }
                                      disabled={
                                        facilityFeatures.length > 0 &&
                                        facilityFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.view,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  Add
                                </Typography>
                                {(() => {
                                  const facilityLevelStates =
                                    getLevelCheckedStates(facilityFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={
                                        facilityLevelStates.allAddChecked
                                      }
                                      indeterminate={
                                        !facilityLevelStates.allAddChecked &&
                                        facilityLevelStates.someAddChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "add",
                                          e.target.checked,
                                          facilityFeatures,
                                        )
                                      }
                                      disabled={
                                        facilityFeatures.length > 0 &&
                                        facilityFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.add,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  Edit
                                </Typography>
                                {(() => {
                                  const facilityLevelStates =
                                    getLevelCheckedStates(facilityFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={
                                        facilityLevelStates.allUpdateChecked
                                      }
                                      indeterminate={
                                        !facilityLevelStates.allUpdateChecked &&
                                        facilityLevelStates.someUpdateChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "update",
                                          e.target.checked,
                                          facilityFeatures,
                                        )
                                      }
                                      disabled={
                                        facilityFeatures.length > 0 &&
                                        facilityFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.update,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                            <MUITableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: "12px",
                                color: "#262626",
                                padding: "8px 16px",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#e6fafc",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#262626",
                                  }}
                                >
                                  Delete
                                </Typography>
                                {(() => {
                                  const facilityLevelStates =
                                    getLevelCheckedStates(facilityFeatures);
                                  return (
                                    <Checkbox
                                      size="small"
                                      checked={
                                        facilityLevelStates.allRemoveChecked
                                      }
                                      indeterminate={
                                        !facilityLevelStates.allRemoveChecked &&
                                        facilityLevelStates.someRemoveChecked
                                      }
                                      onChange={(e) =>
                                        toggleAllPermissions(
                                          "remove",
                                          e.target.checked,
                                          facilityFeatures,
                                        )
                                      }
                                      disabled={
                                        facilityFeatures.length > 0 &&
                                        facilityFeatures.every(
                                          (f) =>
                                            !Boolean(
                                              f?.default_permissions?.delete,
                                            ),
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        margin: 0,
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&&.Mui-disabled": {
                                          color: "#bdbdbd !important",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </MUITableCell>
                          </MUITableRow>
                        </MUITableHead>
                        <MUITableBody>
                          {facilityFeatures.map((feature) => {
                            // Try both string and number keys for feature ID
                            const featureIdKey = String(feature.id);
                            const featureIdNum = Number(feature.id);
                            const state = permissionMatrix[featureIdKey] ||
                              permissionMatrix[featureIdNum] ||
                              permissionMatrix[feature.id] || {
                                view: false,
                                add: false,
                                update: false,
                                remove: false,
                              };
                            return (
                              <MUITableRow key={feature.id}>
                                <MUITableCell
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: "13px",
                                    color: "#262626",
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  {getFeatureDisplayName(
                                    feature.feature_name ||
                                      feature.name ||
                                      String(feature.code ?? feature.id ?? ""),
                                  )}
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.view,
                                      ) && state.view
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "view")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.view,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.add,
                                      ) && state.add
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "add")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.add,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.update,
                                      ) && state.update
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "update")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.update,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                                <MUITableCell
                                  align="center"
                                  sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      Boolean(
                                        feature?.default_permissions?.delete,
                                      ) && state.remove
                                    }
                                    onChange={() =>
                                      togglePermission(feature.id, "remove")
                                    }
                                    disabled={
                                      !Boolean(
                                        feature?.default_permissions?.delete,
                                      )
                                    }
                                    sx={{
                                      color: "#0a829d",
                                      padding: "4px",
                                      "&.Mui-checked": {
                                        color: "#0a829d",
                                      },
                                      "&&.Mui-disabled": {
                                        color: "#bdbdbd !important",
                                        "& .MuiSvgIcon-root": {
                                          backgroundColor: "#e0e0e0",
                                          borderRadius: "4px",
                                        },
                                      },
                                    }}
                                  />
                                </MUITableCell>
                              </MUITableRow>
                            );
                          })}
                        </MUITableBody>
                      </MUITable>
                    </Box>
                  )}
                </Box>
              );
            })()}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", gap: "12px" }}>
          <Button
            onClick={handleClosePermissionsModal}
            sx={{
              backgroundColor: "transparent",
              color: "#737373",
              border: "1px solid #e0e0e0",
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "14px",
              fontWeight: 500,
              padding: "12px 24px",
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                border: "1px solid #bfe5ec",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePermissions}
            disabled={
              permissionsLoading || featuresLoading || permissionsSaving
            }
            sx={{
              background: permissionsSaving ? "#b0c4c8" : "#0a829d",
              color: permissionsSaving ? "#e6fafc" : "white",
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "14px",
              fontWeight: 600,
              padding: "12px 24px",
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": {
                background: permissionsSaving ? "#b0c4c8" : "#085f73",
              },
              "&:disabled": {
                background: "#b0c4c8",
                color: "#e6fafc",
              },
            }}
          >
            {permissionsSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Modal */}
      <AddUserModal
        open={addUserModalOpen}
        onClose={handleAddUserModalClose}
        facilityId={Number(selectedFacility) || 0}
        onUserAdded={handleUserAdded}
        availableFacilities={facilities.map((f) => ({
          id: f.id,
          name: f.facility_name || `Facility ${f.id}`,
          org_name: f.org_name,
          city: f.city,
          state: f.state,
          // Preserve Affable connection flag so modal can conditionally show controls
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          is_affable_connected: (f as any)?.is_affable_connected as
            | boolean
            | undefined,
        }))}
      />

      {/* Edit User Modal */}
      <EditUserModal
        open={editUserModalOpen}
        onClose={handleEditUserModalClose}
        user={userToEdit}
        facilityId={Number(selectedFacility) || 0}
        onUserUpdated={handleUserUpdated}
      />
    </UserManagementContainer>
  );
};

export default UserManagement;
